import { useState, useMemo } from 'react';
import { useStore } from '../../../../hooks/useStore';
import { useAuth } from '../../../../hooks/useAuth';
import { getFinances, getFarmersByHtx, getLoansByHtx, getDebtsByHtx, getHtxDashboard, addFinance, updateFinance, deleteFinance } from '../../../../store';
import { formatCompactCurrency4, formatDate, todayInputValue } from '../../../../utils/format';
import { StatusBadge } from '../../../../components/base/StatusBadge';
import { showToast } from '../../../../components/base/Toast';
import { EmptyState } from '../../../../components/base/EmptyState';
import { confirmModal } from '../../../../components/base/ConfirmModal';
import type { AdvanceRequest, FinanceSource } from '../../../../types';
import { MobileMetricCard, MobileSectionHeader } from '@/components/base/MobileUi';
import { SearchFilterBar } from '@/components/base/SearchFilterBar';

/** A loan's real disbursement/settlement, reshaped into the same
 * FinanceSource-like row the manual Thu/Chi entries use — this is what makes
 * "Đã giải ngân 25 triệu" on Vay & Hạn mức actually show up here instead of
 * "Chưa có giao dịch" (Feedback round 3: the two screens read from
 * completely disconnected data before this). Read-only: it mirrors real
 * loan state, so it has no edit/delete action, unlike a manually-entered row. */
function deriveLoanTransactions(loans: AdvanceRequest[]): FinanceSource[] {
  const rows: FinanceSource[] = [];
  for (const loan of loans) {
    const shortId = loan.id.slice(-6).toUpperCase();
    if (loan.ngayGiaiNgan) {
      rows.push({
        id: `loan-disburse-${loan.id}`,
        farmerId: loan.farmerId,
        loai: 'Chi',
        soTien: loan.soTien,
        moTa: `Giải ngân khoản vay${loan.mucDich ? ` — ${loan.mucDich}` : ''}`,
        ngay: loan.ngayGiaiNgan,
        trangThai: 'Hoàn thành',
        maGiaoDich: `GN-${shortId}`,
        phanLoai: 'Giải ngân',
        readOnly: true,
      });
    }
    if (loan.ngayTatToan) {
      rows.push({
        id: `loan-settle-${loan.id}`,
        farmerId: loan.farmerId,
        loai: 'Thu',
        soTien: loan.soTien,
        moTa: `Tất toán khoản vay${loan.mucDich ? ` — ${loan.mucDich}` : ''}`,
        ngay: loan.ngayTatToan,
        trangThai: 'Hoàn thành',
        maGiaoDich: `TT-${shortId}`,
        phanLoai: 'Trả nợ',
        readOnly: true,
      });
    }
  }
  return rows;
}

const PHAN_LOAI_LIST = ['Tất cả', 'Giải ngân', 'Trả nợ', 'Cấp vật tư', 'Điều chỉnh', 'Khác'];
const TRANG_THAI_LIST = ['Tất cả', 'Hoàn thành', 'Chờ xác nhận', 'Hủy'];
const LOAI_LIST = ['Tất cả', 'Thu', 'Chi'];

const phanLoaiColorMap: Record<string, string> = {
  'Giải ngân': 'bg-red-100 text-red-700',
  'Trả nợ': 'bg-agrida-100 text-agrida-700',
  'Cấp vật tư': 'bg-orange-100 text-orange-700',
  'Điều chỉnh': 'bg-sky-100 text-sky-700',
  'Khác': 'bg-slate-100 text-slate-600',
};

export function TransactionTab() {
  useStore();
  const { user } = useAuth();
  const manualFinances = getFinances();
  const farmers = getFarmersByHtx(user?.htxId);
  const loans = getLoansByHtx(user?.htxId);
  const debts = getDebtsByHtx(user?.htxId);
  const htxDashboard = getHtxDashboard(user?.htxId);
  const finance = htxDashboard?.finance;
  const allocatedPercent = finance && finance.totalLimitVnd > 0 ? Math.round((finance.allocatedVnd / finance.totalLimitVnd) * 100) : 0;
  const loanTransactions = useMemo(() => deriveLoanTransactions(loans), [loans]);
  // Merged, not a replacement — manual Thu/Chi entries (vật tư/điều chỉnh/khác)
  // still matter; the real loan disbursement/settlement rows just fill the gap
  // that used to show up as "Chưa có giao dịch" despite loans already active.
  const finances = useMemo(() => [...manualFinances, ...loanTransactions], [manualFinances, loanTransactions]);
  const tongDuNo = useMemo(() => debts.filter((d) => d.status !== 'Đã tất toán').reduce((s, d) => s + d.conNo, 0), [debts]);
  const [search, setSearch] = useState('');
  const [loaiFilter, setLoaiFilter] = useState('Tất cả');
  const [trangThaiFilter, setTrangThaiFilter] = useState('Tất cả');
  const [loaiGdFilter, setLoaiGdFilter] = useState('Tất cả');
  const [farmerFilter, setFarmerFilter] = useState('Tất cả');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const [editItem, setEditItem] = useState<FinanceSource | null>(null);
  const [form, setForm] = useState({
    farmerId: '',
    loai: 'Thu' as FinanceSource['loai'],
    soTien: '',
    moTa: '',
    ngay: '',
    trangThai: 'Hoàn thành' as FinanceSource['trangThai'],
    nguonGoc: '',
    phanLoai: 'Khác' as FinanceSource['phanLoai'],
  });

  const filtered = useMemo(() => {
    return finances.filter((f) => {
      if (search) {
        const farmer = farmers.find((fa) => fa.id === f.farmerId);
        const q = search.toLowerCase();
        if (!f.moTa.toLowerCase().includes(q) && !farmer?.name.toLowerCase().includes(q) && !(f.maGiaoDich || '').toLowerCase().includes(q)) return false;
      }
      if (loaiFilter !== 'Tất cả' && f.phanLoai !== loaiFilter) return false;
      if (trangThaiFilter !== 'Tất cả' && f.trangThai !== trangThaiFilter) return false;
      if (loaiGdFilter !== 'Tất cả' && f.loai !== loaiGdFilter) return false;
      if (farmerFilter !== 'Tất cả' && f.farmerId !== farmerFilter) return false;
      if (dateFrom && f.ngay < dateFrom) return false;
      if (dateTo && f.ngay > dateTo) return false;
      return true;
    });
  }, [finances, search, loaiFilter, trangThaiFilter, loaiGdFilter, farmerFilter, dateFrom, dateTo, farmers]);

  const getFarmerName = (id: string) => farmers.find((f) => f.id === id)?.name || id;

  const openCreate = () => {
    setEditItem(null);
    setForm({ farmerId: farmers[0]?.id || '', loai: 'Thu', soTien: '', moTa: '', ngay: todayInputValue(), trangThai: 'Hoàn thành', nguonGoc: 'HTX Hòa Phát', phanLoai: 'Khác' });
    setFormOpen(true);
  };

  const openEdit = (item: FinanceSource) => {
    setEditItem(item);
    setForm({ farmerId: item.farmerId, loai: item.loai, soTien: String(Math.abs(item.soTien)), moTa: item.moTa, ngay: item.ngay, trangThai: item.trangThai, nguonGoc: item.nguonGoc || '', phanLoai: item.phanLoai || 'Khác' });
    setFormOpen(true);
  };

  const handleSave = () => {
    const soTien = Number(form.soTien);
    if (!form.farmerId || !soTien || !form.moTa.trim() || !form.ngay) {
      showToast('Vui lòng điền đầy đủ thông tin', 'warning'); return;
    }
    const data = { farmerId: form.farmerId, loai: form.loai, soTien, moTa: form.moTa, ngay: form.ngay, trangThai: form.trangThai, nguonGoc: form.nguonGoc || undefined, maGiaoDich: editItem?.maGiaoDich || `FT${Date.now().toString(36).toUpperCase()}`, phanLoai: form.phanLoai };
    if (editItem) {
      updateFinance(editItem.id, data);
      showToast('Cập nhật giao dịch thành công', 'success');
    } else {
      addFinance(data);
      showToast('Thêm giao dịch thành công', 'success');
    }
    setFormOpen(false);
    setEditItem(null);
  };

  const handleDelete = async (item: FinanceSource) => {
    const ok = await confirmModal({ title: 'Xóa giao dịch', message: `Xóa giao dịch "${item.moTa}"?`, variant: 'danger', confirmText: 'Xóa' });
    if (ok) { deleteFinance(item.id); showToast('Đã xóa giao dịch', 'success'); }
  };

  const totalThu = useMemo(() => filtered.filter((f) => f.loai === 'Thu').reduce((s, f) => s + f.soTien, 0), [filtered]);
  const totalChi = useMemo(() => filtered.filter((f) => f.loai === 'Chi').reduce((s, f) => s + f.soTien, 0), [filtered]);

  return (
    <div className="space-y-4">
      {/* Overview — merges the former separate "Tổng quan" tab: a quick,
          at-a-glance cross-reference against Vay & Hạn mức/Công nợ, right
          where the transactions the reader is about to filter live. */}
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        <MobileMetricCard label="Tổng dư nợ" value={formatCompactCurrency4(tongDuNo)} caption="Các khoản chưa tất toán" tone="sky" icon="ri-money-cny-circle-line" />
        <MobileMetricCard label="Tổng thu" value={formatCompactCurrency4(totalThu)} caption="Tiền vào (theo bộ lọc)" tone="emerald" icon="ri-arrow-down-circle-line" />
        <MobileMetricCard label="Tổng chi" value={formatCompactCurrency4(totalChi)} caption="Tiền ra (theo bộ lọc)" tone="rose" icon="ri-arrow-up-circle-line" />
        <MobileMetricCard label="Chênh lệch" value={formatCompactCurrency4(totalThu - totalChi)} caption="Thu trừ chi" tone={totalThu - totalChi >= 0 ? 'teal' : 'rose'} icon="ri-scales-line" />
      </div>

      {finance && (
        <section>
          <MobileSectionHeader title="Hạn mức HTX" count={`${allocatedPercent}% đã phân bổ`} />
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
            <MobileMetricCard label="Hạn mức tổng" value={formatCompactCurrency4(finance.totalLimitVnd)} caption="Do Agrida cấp cho HTX" tone="sky" icon="ri-bank-card-2-line" />
            <MobileMetricCard label="Đã phân bổ" value={formatCompactCurrency4(finance.allocatedVnd)} caption="Cấp qua hợp đồng bao tiêu" tone="amber" icon="ri-share-forward-line" />
            <MobileMetricCard label="Còn có thể cấp" value={formatCompactCurrency4(finance.remainingToAllocateVnd)} caption="Hạn mức tổng trừ đã phân bổ" tone={finance.remainingToAllocateVnd > 0 ? 'emerald' : 'rose'} icon="ri-funds-line" />
            <MobileMetricCard label="Nông hộ đang vay" value={String(finance.farmersWithLoanCount)} caption={`${finance.loanCount} lần vay (mọi trạng thái)`} tone="teal" icon="ri-group-line" />
          </div>
        </section>
      )}

      <SearchFilterBar
        value={search}
        onChange={setSearch}
        placeholder="Tìm giao dịch"
        selected={loaiFilter === 'Tất cả' ? '' : loaiFilter}
        onSelect={(value) => setLoaiFilter(value || 'Tất cả')}
        options={PHAN_LOAI_LIST.map((item) => ({ value: item === 'Tất cả' ? '' : item, label: item, count: item === 'Tất cả' ? finances.length : finances.filter((finance) => finance.phanLoai === item).length }))}
      >
        <div className="grid grid-cols-2 gap-2">
            <select value={loaiGdFilter} onChange={(e) => setLoaiGdFilter(e.target.value)} className="px-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-agrida-500">
              {LOAI_LIST.map((l) => <option key={l} value={l}>Loại: {l}</option>)}
            </select>
            <select value={trangThaiFilter} onChange={(e) => setTrangThaiFilter(e.target.value)} className="px-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-agrida-500">
              {TRANG_THAI_LIST.map((l) => <option key={l} value={l}>TT: {l}</option>)}
            </select>
            <select value={farmerFilter} onChange={(e) => setFarmerFilter(e.target.value)} className="px-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-agrida-500 max-w-[180px]">
              <option value="Tất cả">Tất cả nông hộ</option>
              {farmers.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
            </select>
          <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="min-w-0 rounded-xl border border-slate-200 px-2 py-2.5 text-xs" />
          <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="min-w-0 rounded-xl border border-slate-200 px-2 py-2.5 text-xs" />
        </div>
      </SearchFilterBar>

      {filtered.length === 0 ? (
        <EmptyState icon="ri-exchange-line" title="Chưa có giao dịch" description="Thêm giao dịch tài chính đầu tiên." />
      ) : (
        <div className="grid grid-cols-1 gap-3">
          {filtered.sort((a, b) => new Date(b.ngay).getTime() - new Date(a.ngay).getTime()).map((fi) => (
            <div key={fi.id} className="bg-white rounded-xl border border-slate-200 p-4">
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="text-sm font-semibold text-slate-900">{fi.moTa}</h3>
                    {fi.phanLoai && (
                      <span className={`text-[11px] px-2 py-0.5 rounded-full font-medium ${phanLoaiColorMap[fi.phanLoai] || 'bg-slate-100 text-slate-600'}`}>
                        {fi.phanLoai}
                      </span>
                    )}
                    <StatusBadge status={fi.loai} />
                    <StatusBadge status={fi.trangThai} />
                  </div>
                  <div className="flex items-center gap-3 text-xs text-slate-400 mt-0.5 flex-wrap">
                    <span>{getFarmerName(fi.farmerId)}</span>
                    <span>·</span>
                    <span>{formatDate(fi.ngay)}</span>
                    {fi.maGiaoDich && <><span>·</span><span className="font-mono">{fi.maGiaoDich}</span></>}
                  </div>
                </div>
                <p className={`text-sm font-bold shrink-0 ${fi.loai === 'Thu' ? 'text-agrida-700' : 'text-red-700'}`}>
                  {fi.loai === 'Thu' ? '+' : '-'}{formatCompactCurrency4(Math.abs(fi.soTien))}
                </p>
              </div>
              <div className="mt-2 flex items-center justify-between">
                {fi.nguonGoc && <span className="text-xs text-slate-400">Nguồn: {fi.nguonGoc}</span>}
                {fi.readOnly ? (
                  <span className="text-[10px] text-slate-400 flex items-center gap-1"><i className="ri-lock-line" /> Tự động từ khoản vay</span>
                ) : (
                  <div className="flex items-center gap-1">
                    <button type="button" onClick={() => openEdit(fi)} className="w-8 h-8 flex items-center justify-center rounded-md text-agrida-700 hover:bg-agrida-50" title="Sửa"><i className="ri-edit-line" /></button>
                    <button type="button" onClick={() => handleDelete(fi)} className="w-8 h-8 flex items-center justify-center rounded-md text-red-600 hover:bg-red-50" title="Xóa"><i className="ri-delete-bin-line" /></button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {formOpen && (
        <div className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center p-0 sm:p-4">
          <div className="absolute inset-0 bg-black/40" onClick={() => setFormOpen(false)} />
          <div className="relative bg-white rounded-t-2xl sm:rounded-2xl shadow-xl w-full sm:max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-slate-100 px-6 py-4 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-slate-900">{editItem ? 'Sửa giao dịch' : 'Thêm giao dịch'}</h3>
              <button type="button" onClick={() => setFormOpen(false)} className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-slate-100 text-slate-400"><i className="ri-close-line text-lg" /></button>
            </div>
            <div className="px-6 py-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Nông hộ</label>
                <select value={form.farmerId} onChange={(e) => setForm({ ...form, farmerId: e.target.value })} className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-agrida-500">
                  {farmers.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Loại</label>
                  <select value={form.loai} onChange={(e) => setForm({ ...form, loai: e.target.value as FinanceSource['loai'] })} className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-agrida-500">
                    <option value="Thu">Thu</option>
                    <option value="Chi">Chi</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Phân loại</label>
                  <select value={form.phanLoai} onChange={(e) => setForm({ ...form, phanLoai: e.target.value as FinanceSource['phanLoai'] })} className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-agrida-500">
                    <option value="Giải ngân">Giải ngân</option>
                    <option value="Trả nợ">Trả nợ</option>
                    <option value="Cấp vật tư">Cấp vật tư</option>
                    <option value="Điều chỉnh">Điều chỉnh</option>
                    <option value="Khác">Khác</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Số tiền</label>
                <input type="number" value={form.soTien} onChange={(e) => setForm({ ...form, soTien: e.target.value })} className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-agrida-500" placeholder="0" />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Mô tả</label>
                <input type="text" value={form.moTa} onChange={(e) => setForm({ ...form, moTa: e.target.value })} className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-agrida-500" placeholder="Mô tả giao dịch" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Ngày</label>
                  <input type="date" value={form.ngay} onChange={(e) => setForm({ ...form, ngay: e.target.value })} className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-agrida-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Nguồn gốc</label>
                  <input type="text" value={form.nguonGoc} onChange={(e) => setForm({ ...form, nguonGoc: e.target.value })} className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-agrida-500" placeholder="Nguồn gốc" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Trạng thái</label>
                <select value={form.trangThai} onChange={(e) => setForm({ ...form, trangThai: e.target.value as FinanceSource['trangThai'] })} className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-agrida-500">
                  <option value="Hoàn thành">Hoàn thành</option>
                  <option value="Chờ xác nhận">Chờ xác nhận</option>
                  <option value="Hủy">Hủy</option>
                </select>
              </div>
            </div>
            <div className="sticky bottom-0 bg-white border-t border-slate-100 px-6 py-4 flex gap-3 justify-end">
              <button type="button" onClick={() => setFormOpen(false)} className="px-4 py-2.5 rounded-xl text-sm font-medium text-slate-700 bg-slate-100 hover:bg-slate-200 transition-colors">Hủy</button>
              <button type="button" onClick={handleSave} className="px-4 py-2.5 rounded-xl text-sm font-medium text-white bg-agrida-700 hover:bg-agrida-800 transition-colors">{editItem ? 'Lưu thay đổi' : 'Thêm mới'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
